import { freshAccessToken } from "./auth";
import { config } from "./config";

/**
 * The backend, as seen from the phone.
 *
 * Mirrors the tvOS `APIClient` deliberately, including the error vocabulary. The two clients talk to
 * the same endpoints with the same failure modes, and requirement 6.6's conflict — "someone else
 * already called this race" — has to mean the same thing in both places, because the two of them are
 * usually the two clients involved.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Extra fields the API attaches to an error, e.g. the winning `finishingOrder` on a 409. */
    readonly detail: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** The token is no longer good, or this user is not in this team. Both mean: sign in again. */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** Requirement 6.6 — first write wins, and this caller lost. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

/** No usable connection. Distinct from a rejection, because only one of them is worth retrying. */
export class OfflineError extends Error {
  constructor() {
    super("Couldn't reach the internet.");
    this.name = "OfflineError";
  }
}

// --- Wire types ---------------------------------------------------------------------------------

export interface Me {
  sub: string;
  email?: string | null;
  name?: string | null;
  teams: TeamSummary[];
}

export interface TeamSummary {
  teamId: string;
  name?: string | null;
  role?: string | null;
  personal?: boolean;
}

export interface Member {
  sub: string;
  role: string;
  name?: string | null;
  email?: string | null;
}

/** Requirements 5.8 and 5.10 — a team and the code that lets someone into it. */
export interface CreatedTeam {
  teamId: string;
  name: string;
  role?: string;
  joinCode: string;
}

/**
 * Requirement 5.11 — everything the confirmation needs to be honest, fetched before anything is
 * written. `leaving` is 5.12's cost, named rather than implied, and `blockedReason` is the one case
 * design §2.7 refuses, so the screen can say so instead of letting the joiner find out on submit.
 */
export interface JoinPreview {
  teamId: string;
  name: string | null;
  alreadyMember: boolean;
  leaving: {
    teamId: string;
    name: string | null;
    /** Nobody would be left behind — the team's history becomes unreachable. Worth saying plainly. */
    lastMember: boolean;
  } | null;
  blockedReason: string | null;
}

export interface JoinResult {
  teamId: string;
  name: string | null;
  role: string;
  /** False when they were already in — following a link twice is normal, not an error. */
  joined: boolean;
  left: string | null;
}

/** How far a vehicle's photograph has got. Requirement 7.6 makes `fallback` a success, not a failure. */
export type CutoutState = "none" | "pending" | "processing" | "ready" | "fallback" | "failed";

export interface Vehicle {
  vehicleId: string;
  name?: string | null;
  racingTeam?: string | null;
  /** Requirement 7.13 — which class this races in. Null is unclassified, a real class (7.14). */
  vehicleClass?: string | null;
  isRetired: boolean;
  photoKey?: string | null;
  cutoutKey?: string | null;
  cutoutState?: CutoutState | null;
  /** Why a mask was rejected. Worth showing: it tells someone how to re-shoot. */
  cutoutReason?: string | null;
  /** Requirement 7.6 — use the framed photograph rather than a mangled silhouette. */
  usesFramedFallback?: boolean;
  /**
   * Signed CloudFront URLs, minted per response.
   *
   * Keys are useless to a client: the bucket is private and the distribution refuses unsigned requests
   * (design §6.2). These expire, so they are not worth caching in local storage.
   */
  cutoutUrl?: string | null;
  photoUrl?: string | null;
  /**
   * The announcer's spoken name for this car — the one that will actually play.
   *
   * Two slots sit behind it. `recordedName*` is a human recording and `autoName*` is the synthesised
   * fallback; a recording wins when there is one, and the server resolves that so neither client has to.
   * Keeping both is what makes recording reversible: removing a take falls back rather than silencing the
   * car, and a silent car is one the announcer skips entirely.
   */
  nameKey?: string | null;
  nameUrl?: string | null;
  recordedNameKey?: string | null;
  recordedNameUrl?: string | null;
  autoNameKey?: string | null;
  autoNameUrl?: string | null;
  /** `recorded` or `auto` — which of the two slots `nameUrl` points at. */
  nameVoice?: "recorded" | "auto" | null;
  nameSeconds?: string | null;
}

export interface PresignedUpload {
  uploadUrl: string;
  key: string;
  /** Must be reproduced verbatim — `content-type` is part of the signature S3 checks. */
  headers: Record<string, string>;
  expiresIn: number;
}

export interface VehiclePhotoUpload extends PresignedUpload {
  /**
   * Where to put a downscaled copy, for the segmentation model.
   *
   * Bedrock refuses a full-resolution phone photograph twice over — once on input pixels and once on the
   * size of the PNG it would return — so the original alone cannot be segmented. Fill this whenever the
   * long edge exceeds `inferenceMaxEdge`; the backend treats a missing copy as "use the original". See
   * `downscale.ts`.
   */
  inference: PresignedUpload;
  inferenceMaxEdge: number;
}

export interface Device {
  deviceId: string;
  deviceName?: string | null;
  pairedAt?: string | null;
}

export interface Heat {
  round: number;
  index: number;
  participants: string[];
  finishingOrder: string[] | null;
  state: "pending" | "complete";
  mediaCount: number;
}

/**
 * Requirement 8.9 — a collection from day one, with exactly one entry in v1.
 *
 * Requirement 8.8 puts transcoding out of scope for now and has tvOS play the original so the Apple TV
 * gets the full-quality HEVC the phone recorded. Reading this as a list rather than as a single `url`
 * is what makes a derived H.264 copy an additive change later instead of a migration: this code already
 * picks the best variant it can play, so a second entry needs no new field and no backfill.
 */
export interface PlaybackVariant {
  name: string;
  contentType: string;
  bytes: number | null;
  /** Signed CloudFront URL, minted per response. Expires — do not persist it. */
  url: string | null;
}

export interface MediaRecord {
  mediaId: string;
  kind: "photo" | "video";
  /** Null for requirement 8.2's tournament-level media: the track build, the crowd, the trophy shot. */
  round: number | null;
  index: number | null;
  vehicleIds: string[];
  bytes: number | null;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  capturedAt: string | null;
  createdAt: string | null;
  /** Set when an undo reparented this clip off a heat (requirement 4.6), so it can be re-filed. */
  movedFrom: string | null;
  posterUrl: string | null;
  playbackVariants: PlaybackVariant[];
}

/** Which heat a clip belongs to. Both fields absent means the tournament itself (8.2). */
export interface MediaTarget {
  round?: number;
  index?: number;
}

export interface MultipartUpload {
  mediaId: string;
  kind: "photo" | "video";
  key: string;
  uploadId: string;
  parts: { partNumber: number; url: string }[];
  partSize: number;
  expiresIn: number;
  /** Empty, and that is load-bearing — see `uploadMultipart`. */
  headers: Record<string, string>;
  poster: PresignedUpload;
}

export interface CompletedPart {
  partNumber: number;
  eTag: string;
}

export interface ChampionReel {
  tournamentId: string;
  champion: string | null;
  themeId?: string | null;
  clips: (MediaRecord & { position: number })[];
}

export interface VehicleRace {
  tournamentId: string;
  tournamentName: string | null;
  round: number;
  index: number;
  position: number;
  fieldSize: number;
  advanced: boolean;
  racedAt: string | null;
  media: MediaRecord[];
}

export interface Entrant {
  seed: number;
  vehicleId: string;
  name?: string | null;
}

/**
 * A member's prediction for one round. Requirements 9.1 and 9.2.
 *
 * Not called `Pick`: that is a TypeScript built-in utility type, and shadowing it breaks every
 * `Pick<T, K>` in the file.
 *
 * `advancers` is a flat list for the whole round rather than a map keyed by heat, because that is what
 * 9.1 describes — you pick the round's advancers — and unordered per 9.2. The server still checks the
 * count per heat, so an incoherent pick (two from one race, none from its neighbour) is refused.
 *
 * `round: 0` is requirement 9.3's champion pick, which has a `champion` and no advancers.
 */
export interface MemberPick {
  round: number;
  sub: string;
  advancers: string[];
  champion: string | null;
}

export interface Score {
  sub: string;
  points: number;
  roundPoints: number;
  championBonus: number;
  correctPicks: number;
  scoredPicks: number;
}

export interface LeaderboardLine extends Score {
  championPick: string | null;
  accuracy: number;
  /** Requirement 10.5 — under 25 scored selections the figure is shown but flagged, never hidden. */
  provisional: boolean;
  name: string | null;
  picture: string | null;
}

export interface Leaderboard {
  tournamentId: string;
  maximumPoints: number;
  championBonus: number;
  lines: LeaderboardLine[];
}

/** Requirement 9.3's champion pick is round zero — not a round, and it has no advancers. */
export const CHAMPION_ROUND = 0;

/**
 * One member's all-time record. Requirements 10.3 and 10.4.
 *
 * Ranked by `victories` then `accuracy` — never by `points`. Cumulative points measures attendance: a
 * member who has played twice could never catch one who has played twenty however well they predict, so a
 * newcomer would be permanently last regardless of skill. `points` is carried for display only.
 */
export interface MemberRecord {
  sub: string;
  name: string | null;
  picture: string | null;
  victories: number;
  tournamentsPlayed: number;
  points: number;
  correctPicks: number;
  scoredPicks: number;
  championPicks: number;
  bestScore: number;
  accuracy: number;
  averagePoints: number;
  /** Requirement 10.5 — under 25 scored selections. Shown and flagged, never hidden. */
  provisional: boolean;
  /** In the team but has not predicted yet. Being new is not the same as being absent. */
  unplayed: boolean;
}

export interface MemberRecords {
  tournaments: number;
  minimumScoredSelections: number;
  lines: MemberRecord[];
}

/**
 * Requirement 10.6 — one truck's place in the garage Hall of Fame.
 *
 * Named to match the tvOS type, which could not be called `VehicleRecord` because the engine already owns
 * that name for the seeding figures in Milestone 13. Same underlying facts, different purpose: one ranks a
 * garage for a family to look at, the other feeds a draw.
 */
export interface HallOfFameEntry {
  vehicleId: string;
  name: string | null;
  isRetired: boolean;
  cutoutState: CutoutState | null;
  heatsRaced: number;
  heatsWon: number;
  tournamentsEntered: number;
  tournamentsWon: number;
  winRate: number;
  averageFinish: number;
  longestStreak: number;
  provisional: boolean;
  /** Only on the single-vehicle read: every heat it has ever run, newest first. */
  races?: VehicleRaceRecord[];
}

export interface VehicleRaceRecord {
  tournamentId: string;
  tournamentName: string | null;
  round: number;
  index: number;
  position: number;
  fieldSize: number;
  advanced: boolean;
  racedAt: string | null;
}

/** A completed tournament, from the immutable snapshot written when its final was called. */
export interface PastTournament {
  tournamentId: string;
  name: string | null;
  themeID: string | null;
  size: number;
  width: number;
  champion: string | null;
  championName: string | null;
  createdAt: string | null;
  completedAt: string | null;
  maximumPoints: number;
  /** A list, because a tie for top of the prediction board is a real outcome. */
  winners: { sub: string; name: string | null; picture: string | null }[];
  standings: (LeaderboardLine & { championPick: string | null })[];
}

/** Access pattern T1 — the whole tournament in one round trip. */
export interface Tournament {
  tournamentId: string;
  teamId: string;
  name?: string | null;
  themeID?: string | null;
  size: number;
  width: number;
  roundCount: number;
  totalHeatCount: number;
  state: "live" | "complete";
  champion?: string | null;
  /** Design §5.3 — bumped on every state-changing write, so a client can detect a missed event. */
  version: number;
  commitLog: string[];
  entrants: Entrant[];
  heats: Heat[];
  /**
   * Requirement 9.4 — **already filtered by the server**. Your own picks always, everyone else's only
   * once their round has begun. So this list being short is not a bug and the client must not treat a
   * missing pick as "nobody picked": it may mean "not yet yours to see".
   */
  picks: MemberPick[];
  scores: Score[];
  nextHeat: { round: number; index: number } | null;
}

export interface TournamentSummary {
  tournamentId: string;
  name?: string | null;
  size: number;
  width: number;
  state: "live" | "complete";
  champion?: string | null;
  createdAt?: string | null;
}

// --- Transport ----------------------------------------------------------------------------------

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await freshAccessToken();
  if (!token) throw new ApiError(401, "Signed out");

  const headers: Record<string, string> = {
    // The **access** token, not the id token: the authorizer verifies it with Cognito's GetUser,
    // which rejects an id token outright. See auth.ts.
    authorization: `Bearer ${token}`,
  };
  if (body !== undefined) headers["content-type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new OfflineError();
  }

  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    const { error, ...detail } = payload;
    throw new ApiError(
      response.status,
      typeof error === "string" ? error : `Request failed (${response.status})`,
      detail,
    );
  }
  return payload as T;
}

const encode = encodeURIComponent;

/**
 * Sends bytes straight to S3 against a presigned URL. Design §6.3.
 *
 * Not `request` — and not authenticated. The signature *is* the authorization, so attaching a bearer
 * token would be sending a credential to a host that has no business seeing it. Only the headers the
 * presign covered may be sent: S3 signs `content-type`, so an extra header, a changed one, or a missing
 * one is a 403 with a body that does not say which.
 *
 * `onProgress` needs XHR. `fetch` still cannot report upload progress, and a parent standing in a
 * driveway on a phone uploading a photo over cellular is exactly the case where a progress bar is the
 * difference between waiting and force-quitting.
 */
export function uploadBytes(
  upload: PresignedUpload,
  body: Blob,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", upload.uploadUrl, true);
    for (const [name, value] of Object.entries(upload.headers)) xhr.setRequestHeader(name, value);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve();
        return;
      }
      // A 307 here means the presign named the global endpoint instead of the regional one, and the
      // redirect drops the body. Worth naming, because the symptom otherwise looks like a bad file.
      reject(new ApiError(xhr.status, `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new OfflineError());
    xhr.onabort = () => reject(new OfflineError());
    xhr.send(body);
  });
}

/**
 * Uploads a file to S3 in parts, against URLs the API already signed. Requirement 8.5, design §6.3.
 *
 * Sequential rather than parallel. A phone on domestic wifi does not go faster with four sockets open,
 * and one part at a time gives a progress bar that means something — the alternative reports four
 * numbers that finish out of order.
 *
 * **No `Content-Type` header on a part.** `upload_part` does not sign one — the type was fixed when the
 * upload was created — so adding one breaks the signature. `upload.headers` is empty for exactly that
 * reason, and it is iterated rather than ignored so the day it stops being empty this still works.
 *
 * Returns the parts with the ETags `CompleteMultipartUpload` needs. Reading `ETag` off a cross-origin
 * response only works because the bucket's CORS configuration exposes it; without that this returns
 * nulls and the upload can never be completed.
 */
export async function uploadMultipart(
  upload: MultipartUpload,
  file: Blob,
  onProgress?: (fraction: number) => void,
): Promise<CompletedPart[]> {
  const completed: CompletedPart[] = [];
  let uploaded = 0;

  for (const part of upload.parts) {
    const start = (part.partNumber - 1) * upload.partSize;
    const chunk = file.slice(start, Math.min(start + upload.partSize, file.size));
    const eTag = await new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", part.url, true);
      for (const [name, value] of Object.entries(upload.headers)) xhr.setRequestHeader(name, value);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress?.((uploaded + event.loaded) / file.size);
      };
      xhr.onload = () => {
        const tag = xhr.getResponseHeader("ETag");
        if (xhr.status >= 200 && xhr.status < 300 && tag) {
          resolve(tag);
          return;
        }
        reject(
          new ApiError(
            xhr.status,
            tag ? `Upload failed (${xhr.status})` : "The upload couldn't be verified.",
          ),
        );
      };
      xhr.onerror = () => reject(new OfflineError());
      xhr.onabort = () => reject(new OfflineError());
      xhr.send(chunk);
    });
    uploaded += chunk.size;
    completed.push({ partNumber: part.partNumber, eTag });
  }

  onProgress?.(1);
  return completed;
}

export const api = {
  /** Requirement 5.3 — provisions a personal team on first call, so this is also sign-up completion. */
  me: () => request<Me>("GET", "/me"),

  team: (teamId: string) => request<TeamSummary>("GET", `/teams/${encode(teamId)}`),
  /** Requirement 5.8. No team in the path, because there is no team to be a member of yet. */
  createTeam: (name: string) => request<CreatedTeam>("POST", "/teams", { name }),
  /** Requirement 5.9 — owners, any time. */
  renameTeam: (teamId: string, name: string) =>
    request<{ teamId: string; name: string }>("PATCH", `/teams/${encode(teamId)}`, { name }),
  /** Requirement 5.10 — mints a new code and kills every link already sent. */
  invite: (teamId: string) => request<CreatedTeam>("POST", `/teams/${encode(teamId)}/invite`),
  /** Requirement 5.12's departure on its own. Members may leave; only the last owner of a
   *  populated team may not. */
  leaveTeam: (teamId: string) => request<{ left: string }>("POST", `/teams/${encode(teamId)}/leave`),
  /**
   * Requirement 5.11 — what the confirmation is built from. Access pattern A5, and the one team read
   * that works for someone who is not a member: that is what being invited means.
   *
   * The code is **not** `encode`d. It comes from `secrets.token_urlsafe`, so it is already URL-safe by
   * construction, and encoding it would turn a valid code into one the server cannot find.
   */
  previewJoin: (code: string) => request<JoinPreview>("GET", `/join/${code}`),
  join: (code: string) => request<JoinResult>("POST", `/join/${code}`),
  members: (teamId: string) => request<{ members: Member[] }>("GET", `/teams/${encode(teamId)}/members`),
  setRole: (teamId: string, sub: string, role: string) =>
    request<unknown>("PUT", `/teams/${encode(teamId)}/members/${encode(sub)}`, { role }),
  removeMember: (teamId: string, sub: string) =>
    request<unknown>("DELETE", `/teams/${encode(teamId)}/members/${encode(sub)}`),

  vehicles: (teamId: string) => request<{ vehicles: Vehicle[] }>("GET", `/teams/${encode(teamId)}/vehicles`),
  addVehicle: (teamId: string, name: string, vehicleClass?: string) =>
    request<Vehicle>("POST", `/teams/${encode(teamId)}/vehicles`, {
      name,
      // Omitted rather than sent empty when there is none: on create, empty would be saying something.
      ...(vehicleClass ? { vehicleClass } : {}),
    }),
  /** Rename, relabel, or retire. Requirement 7.9 — **there is no delete**, and no route for one. */
  updateVehicle: (
    teamId: string,
    vehicleId: string,
    // `vehicleClass` accepts an empty string on purpose: the server reads present-and-empty as a
    // deliberate clear, which is how a truck is taken out of a class it does not belong in.
    changes: Partial<Pick<Vehicle, "name" | "racingTeam" | "isRetired" | "vehicleClass">>,
  ) =>
    request<Vehicle>("PATCH", `/teams/${encode(teamId)}/vehicles/${encode(vehicleId)}`, changes),

  /**
   * Milestone 9. Two calls around an upload that does not pass through the API at all: the bytes go from
   * this browser straight to S3 (design §6.3), which is why `startPhoto` hands back a URL rather than
   * accepting a file.
   */
  startPhoto: (teamId: string, vehicleId: string, contentType: string) =>
    request<VehiclePhotoUpload>(
      "POST",
      `/teams/${encode(teamId)}/vehicles/${encode(vehicleId)}/photo`,
      { contentType },
    ),
  finishPhoto: (teamId: string, vehicleId: string, key: string) =>
    request<Vehicle>(
      "POST",
      `/teams/${encode(teamId)}/vehicles/${encode(vehicleId)}/photo/complete`,
      { key },
    ),
  /**
   * The announcer's spoken name, in the same two calls as a photograph and for the same reason: the bytes
   * go from this browser straight to S3 without passing through the API (design §6.3).
   */
  startName: (teamId: string, vehicleId: string, contentType: string) =>
    request<PresignedUpload & { maxBytes: number }>(
      "POST",
      `/teams/${encode(teamId)}/vehicles/${encode(vehicleId)}/name`,
      { contentType },
    ),
  finishName: (
    teamId: string,
    vehicleId: string,
    key: string,
    seconds?: number,
    source = "recorded",
  ) =>
    request<{ nameKey: string; nameUrl: string | null }>(
      "POST",
      `/teams/${encode(teamId)}/vehicles/${encode(vehicleId)}/name/complete`,
      { key, seconds, source },
    ),
  /** So a bad take can be undone rather than only recorded over. */
  deleteName: (teamId: string, vehicleId: string) =>
    request<{ nameKey: null }>(
      "DELETE",
      `/teams/${encode(teamId)}/vehicles/${encode(vehicleId)}/name`,
    ),

  /** Task 9.8 — re-run every cutout, possible only because originals are kept (requirement 7.2). */
  reprocessCutouts: (teamId: string) =>
    request<{ requested: number; withPhotos: number }>(
      "POST",
      `/teams/${encode(teamId)}/vehicles/reprocess`,
    ),

  /** Design §2.2 — the family types the code the television is showing. */
  redeemCode: (teamId: string, code: string) =>
    request<{ teamName?: string | null }>("POST", `/teams/${encode(teamId)}/pair/redeem`, { code }),
  devices: (teamId: string) => request<{ devices: Device[] }>("GET", `/teams/${encode(teamId)}/devices`),
  revokeDevice: (teamId: string, deviceId: string) =>
    request<unknown>("DELETE", `/teams/${encode(teamId)}/devices/${encode(deviceId)}`),

  tournaments: (teamId: string) =>
    request<{ tournaments: TournamentSummary[] }>("GET", `/teams/${encode(teamId)}/tournaments`),
  tournament: (teamId: string, tournamentId: string) =>
    request<Tournament>("GET", `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}`),
  createTournament: (
    teamId: string,
    body: { name: string; size: number; width: number; seededRoster: string[]; themeID?: string },
  ) => request<Tournament>("POST", `/teams/${encode(teamId)}/tournaments`, body),
  /**
   * Access pattern T7. Set-once and keyed by heat id, so this is safe to retry (requirement 6.6): an
   * identical order on an already-decided heat comes back 200, and only a *different* one is a 409.
   */
  commitHeat: (teamId: string, tournamentId: string, round: number, index: number, finishingOrder: string[]) =>
    request<Tournament>(
      "POST",
      `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}/heats/${round}/${index}`,
      { finishingOrder },
    ),
  /** Requirement 4.4 — undo-last only. */
  undoLast: (teamId: string, tournamentId: string) =>
    request<Tournament>("DELETE", `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}/heats/last`),

  // --- Media (Milestone 10) --------------------------------------------------------------------

  /**
   * Access patterns M1 and M2 from one endpoint. No filter is everything; `{round, index}` is one
   * heat (8.1); `scope=tournament` is the track build and the trophy shot (8.2).
   */
  media: (teamId: string, tournamentId: string, target?: MediaTarget | "tournament") => {
    const base = `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}/media`;
    if (target === "tournament") return request<{ media: MediaRecord[] }>("GET", `${base}?scope=tournament`);
    if (target && target.round !== undefined && target.index !== undefined) {
      return request<{ media: MediaRecord[] }>("GET", `${base}?round=${target.round}&index=${target.index}`);
    }
    return request<{ media: MediaRecord[] }>("GET", base);
  },

  startMedia: (
    teamId: string,
    tournamentId: string,
    body: { contentType: string; size: number } & MediaTarget,
  ) =>
    request<MultipartUpload>(
      "POST",
      `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}/media/start`,
      body,
    ),

  finishMedia: (
    teamId: string,
    tournamentId: string,
    body: {
      mediaId: string;
      key: string;
      uploadId: string;
      contentType: string;
      parts: CompletedPart[];
      durationSeconds?: number;
      width?: number;
      height?: number;
      capturedAt?: string;
    } & MediaTarget,
  ) =>
    request<MediaRecord>(
      "POST",
      `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}/media/complete`,
      body,
    ),

  /** Stops a half-finished upload being billed. Fire-and-forget: failing to tidy up is not an error. */
  abandonMedia: (teamId: string, tournamentId: string, key: string, uploadId: string) =>
    request<unknown>(
      "POST",
      `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}/media/abandon`,
      { key, uploadId },
    ),

  /** Requirement 8.7 — reassign between heats, or to and from tournament level. */
  moveMedia: (teamId: string, tournamentId: string, mediaId: string, target: MediaTarget) =>
    request<MediaRecord>(
      "POST",
      `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}/media/${encode(mediaId)}/move`,
      target,
    ),

  /**
   * Requirement 8.7's delete — the only thing in the app that removes content, and requirement 8.14
   * makes it explicit-only. Recoverable for thirty days because the bucket is versioned (8.15).
   */
  deleteMedia: (teamId: string, tournamentId: string, mediaId: string) =>
    request<{ ok: boolean }>(
      "DELETE",
      `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}/media/${encode(mediaId)}`,
    ),

  /** Requirement 8.4 — the winner's heats, in order. */
  championReel: (teamId: string, tournamentId: string) =>
    request<ChampionReel>(
      "GET",
      `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}/media/reel`,
    ),

  // --- Predictions (Milestone 11) ---------------------------------------------------------------

  /**
   * Access pattern R1. `PUT` because a member has one pick per round and re-submitting replaces it.
   *
   * Refused once the round has started (9.5) rather than queued — a tournament never waits on a person,
   * and silently storing a pick that cannot score would leave someone believing they were in the round.
   */
  submitPick: (
    teamId: string,
    tournamentId: string,
    body: { round: number; advancers?: string[]; champion?: string },
  ) =>
    request<MemberPick & { state: string }>(
      "PUT",
      `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}/picks`,
      body,
    ),

  /** Requirement 10.1 — the running prediction leaderboard. */
  leaderboard: (teamId: string, tournamentId: string) =>
    request<Leaderboard>(
      "GET",
      `/teams/${encode(teamId)}/tournaments/${encode(tournamentId)}/leaderboard`,
    ),

  // --- All-time records (Milestone 12) ----------------------------------------------------------
  //
  // Every one of these is arithmetic over immutable items at read time. Requirement 10.8 rules out
  // maintained totals because they drift, so there is nothing cached and nothing to invalidate.

  /** Access pattern R5 — requirement 10.2, this team only. Cross-team ranking would breach 5.1. */
  memberRecords: (teamId: string) =>
    request<MemberRecords>("GET", `/teams/${encode(teamId)}/records/members`),

  /** Requirement 10.6 — the Hall of Fame, retired vehicles included. */
  hallOfFame: (teamId: string) =>
    request<{ vehicles: HallOfFameEntry[] }>("GET", `/teams/${encode(teamId)}/records/vehicles`),

  /** Requirement 10.7 — every completed tournament, newest first. */
  history: (teamId: string) =>
    request<{ tournaments: PastTournament[] }>("GET", `/teams/${encode(teamId)}/records/history`),

  /** Access patterns G3, G4 and G5 for one vehicle. */
  vehicleRecord: (teamId: string, vehicleId: string) =>
    request<HallOfFameEntry>(
      "GET",
      `/teams/${encode(teamId)}/vehicles/${encode(vehicleId)}/record`,
    ),

  /** Requirement 8.3 — every race a truck has appeared in, across all tournaments. */
  vehicleMedia: (teamId: string, vehicleId: string) =>
    request<{ vehicleId: string; races: VehicleRace[] }>(
      "GET",
      `/teams/${encode(teamId)}/vehicles/${encode(vehicleId)}/media`,
    ),
};
